import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiService] = useState(new ApiService());

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
      const storedHospital = JSON.parse(localStorage.getItem('hospital') || 'null');

      if (token && storedUser && storedHospital) {
        setUser(storedUser);
        setHospital(storedHospital);

        // Fetch latest data from backend on page load
        try {
          const fresh = await apiService.refreshUser();
          setUser(fresh.user);
          setHospital(fresh.hospital);
          localStorage.setItem('user', JSON.stringify(fresh.user));
          localStorage.setItem('hospital', JSON.stringify(fresh.hospital));
        } catch (err) {
          // Token was revoked (another device logged in) → force logout
          console.error('Session expired:', err);
          logout();
          return;
        }
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (phoneNumber, mpin, fcmToken) => {
    try {
      // Login API returns: { token, user: {..., hospital_id: 123}, hospital: {...} }
      const response = await apiService.login(phoneNumber, mpin, fcmToken);
      const { token, user: userData, hospital: hospitalData } = response;

      // Store auth data

      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('hospital', JSON.stringify(hospitalData));

      setUser(userData);
      setHospital(hospitalData);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    await apiService.logout();
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('hospital');
    setUser(null);
    setHospital(null);
  };

  const updateAuth = (newUser, newHospital, newToken) => {
    setUser(newUser);
    setHospital(newHospital);
    localStorage.setItem('user', JSON.stringify(newUser));
    localStorage.setItem('hospital', JSON.stringify(newHospital));
    localStorage.setItem('authToken', newToken);
  };
  const setPasswordfunc = async (phoneNumber, newMpin) => {
    try {
      const data = await apiService.send_otp(phoneNumber, newMpin);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };


  const resetpasswordfunc = async (phoneNumber, otp) => {
    try {
      const data = await apiService.verifyOtp(phoneNumber, otp);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    hospital,
    loading,
    login,
    logout,
    updateAuth,
    setPasswordfunc,
    resetpasswordfunc,
    apiService,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || user?.is_staff || user?.role === 'ADMIN' || false,
    isSuperuser: user?.is_superuser || user?.is_staff || (user?.is_creator && user?.is_approver && user?.is_responder) || false,
    isApprover: user?.is_approver || false,
    isResponder: user?.is_responder || false,
    isCreator: user?.is_creator || false,
    isRaiser: user?.is_raiser || false,

  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };
